import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Alert,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  AccountBalance as BankIcon,
} from '@mui/icons-material';
import { financeService } from '../services/financeService';
import { useSubmitLock } from '../hooks/useSubmitLock';

const SUPPLIER_CATEGORIES = [
  'materia_prima',
  'servicos',
  'equipamentos',
  'embalagens',
  'transporte',
  'tecnologia',
  'marketing',
  'outros'
];

const CATEGORY_LABELS = {
  materia_prima: 'Matéria Prima',
  servicos: 'Serviços',
  equipamentos: 'Equipamentos',
  embalagens: 'Embalagens',
  transporte: 'Transporte',
  tecnologia: 'Tecnologia',
  marketing: 'Marketing',
  outros: 'Outros'
};

const BRAZILIAN_STATES = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
  'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
  'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];

function SuppliersTab() {
  const [suppliers, setSuppliers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState(undefined);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [currentSupplier, setCurrentSupplier] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    legal_name: '',
    document: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    bank_name: '',
    bank_account: '',
    pix_key: '',
    category: '',
    is_active: true,
    notes: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, runSubmit] = useSubmitLock();

  const loadSuppliers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await financeService.getSuppliersPage({
        q: searchQuery,
        category: categoryFilter,
        is_active: activeFilter,
        offset: page * rowsPerPage,
        limit: rowsPerPage
      });
      setSuppliers(response.data || []);
      setTotal(response.total || 0);
    } catch (err) {
      console.error('Erro ao carregar fornecedores:', err);
      setError('Erro ao carregar fornecedores. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchQuery, categoryFilter, activeFilter]);

  useEffect(() => {
    loadSuppliers();
  }, [loadSuppliers]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleCategoryFilter = (event) => {
    setCategoryFilter(event.target.value);
    setPage(0);
  };

  const handleActiveFilter = (event) => {
    const value = event.target.value;
    setActiveFilter(value === '' ? undefined : value === 'true');
    setPage(0);
  };

  const handleOpenDialog = (mode, supplier = null) => {
    setDialogMode(mode);
    setCurrentSupplier(supplier);
    if (mode === 'edit' && supplier) {
      setFormData({
        name: supplier.name || '',
        legal_name: supplier.legal_name || '',
        document: supplier.document || '',
        email: supplier.email || '',
        phone: supplier.phone || '',
        address: supplier.address || '',
        city: supplier.city || '',
        state: supplier.state || '',
        zip_code: supplier.zip_code || '',
        bank_name: supplier.bank_name || '',
        bank_account: supplier.bank_account || '',
        pix_key: supplier.pix_key || '',
        category: supplier.category || '',
        is_active: supplier.is_active !== undefined ? supplier.is_active : true,
        notes: supplier.notes || ''
      });
    } else {
      setFormData({
        name: '',
        legal_name: '',
        document: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        bank_name: '',
        bank_account: '',
        pix_key: '',
        category: '',
        is_active: true,
        notes: ''
      });
    }
    setOpenDialog(true);
    setError('');
    setSuccess('');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentSupplier(null);
    setError('');
    setSuccess('');
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const cleanFormData = (data) => {
    // Remove campos vazios e converte strings vazias para null
    const cleaned = {};
    Object.keys(data).forEach(key => {
      const value = data[key];
      // Manter booleans (como is_active) mesmo se false
      if (typeof value === 'boolean') {
        cleaned[key] = value;
      } else if (value !== '' && value !== null && value !== undefined) {
        cleaned[key] = value;
      }
    });
    return cleaned;
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    if (!formData.name.trim()) {
      setError('Nome é obrigatório');
      return;
    }

    // Validar email se fornecido
    if (formData.email && formData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setError('Email inválido');
        return;
      }
    }

    try {
      const cleanedData = cleanFormData(formData);
      
      if (dialogMode === 'create') {
        await financeService.createSupplier(cleanedData);
        setSuccess('Fornecedor criado com sucesso!');
      } else {
        await financeService.updateSupplier(currentSupplier.id, cleanedData);
        setSuccess('Fornecedor atualizado com sucesso!');
      }
      handleCloseDialog();
      loadSuppliers();
    } catch (err) {
      console.error('Erro ao salvar fornecedor:', err);
      const errorMessage = err.response?.data?.detail 
        || (Array.isArray(err.response?.data) ? err.response.data.map(e => e.msg).join(', ') : null)
        || 'Erro ao salvar fornecedor. Tente novamente.';
      setError(errorMessage);
    }
  };

  const handleDelete = async (supplier) => {
    if (!window.confirm(`Tem certeza que deseja excluir o fornecedor "${supplier.name}"?`)) {
      return;
    }

    try {
      await financeService.deleteSupplier(supplier.id);
      setSuccess('Fornecedor excluído com sucesso!');
      loadSuppliers();
    } catch (err) {
      console.error('Erro ao excluir fornecedor:', err);
      setError(err.response?.data?.detail || 'Erro ao excluir fornecedor. Tente novamente.');
    }
  };

  const formatDocument = (doc) => {
    if (!doc) return '-';
    const cleaned = doc.replace(/\D/g, '');
    if (cleaned.length === 11) {
      // CPF: 000.000.000-00
      return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (cleaned.length === 14) {
      // CNPJ: 00.000.000/0000-00
      return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return doc;
  };

  const formatPhone = (phone) => {
    if (!phone) return '-';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    } else if (cleaned.length === 10) {
      return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    return phone;
  };

  const handlePhoneChange = (value) => {
    // Remove tudo que não é número
    const cleaned = value.replace(/\D/g, '');
    // Limita a 11 dígitos
    const limited = cleaned.substring(0, 11);
    handleFormChange('phone', limited);
  };

  const handleDocumentChange = (value) => {
    // Remove tudo que não é número
    const cleaned = value.replace(/\D/g, '');
    // Limita a 14 dígitos (CNPJ)
    const limited = cleaned.substring(0, 14);
    handleFormChange('document', limited);
  };

  const handleZipCodeChange = (value) => {
    // Remove tudo que não é número
    const cleaned = value.replace(/\D/g, '');
    // Limita a 8 dígitos
    const limited = cleaned.substring(0, 8);
    handleFormChange('zip_code', limited);
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Card 
        elevation={0}
        sx={{ 
          mb: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 3
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <BusinessIcon sx={{ fontSize: 32 }} />
                <Typography variant="h5" fontWeight="bold">
                  Fornecedores
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {total > 0 ? `${total} fornecedor${total !== 1 ? 'es' : ''} cadastrado${total !== 1 ? 's' : ''}` : 'Nenhum fornecedor cadastrado'}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('create')}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.3)',
                },
              }}
            >
              Novo Fornecedor
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar por nome, documento, email..."
                value={searchQuery}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Categoria</InputLabel>
                <Select
                  value={categoryFilter}
                  onChange={handleCategoryFilter}
                  label="Categoria"
                >
                  <MenuItem value="">Todas</MenuItem>
                  {SUPPLIER_CATEGORIES.map(cat => (
                    <MenuItem key={cat} value={cat}>{CATEGORY_LABELS[cat]}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={activeFilter === undefined ? '' : activeFilter.toString()}
                  onChange={handleActiveFilter}
                  label="Status"
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="true">Ativos</MenuItem>
                  <MenuItem value="false">Inativos</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Documento</TableCell>
              <TableCell>Categoria</TableCell>
              <TableCell>Contato</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Transações</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                  <CircularProgress size={40} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Carregando fornecedores...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : suppliers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                  <BusinessIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Nenhum fornecedor encontrado
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {searchQuery || categoryFilter || activeFilter !== undefined
                      ? 'Tente ajustar os filtros de busca'
                      : 'Clique em "Novo Fornecedor" para cadastrar o primeiro'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              suppliers.map(supplier => (
                <TableRow key={supplier.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {supplier.name}
                      </Typography>
                      {supplier.legal_name && supplier.legal_name !== supplier.name && (
                        <Typography variant="caption" color="text.secondary">
                          {supplier.legal_name}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDocument(supplier.document)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {supplier.category ? (
                      <Chip
                        label={CATEGORY_LABELS[supplier.category] || supplier.category}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {supplier.phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PhoneIcon fontSize="small" sx={{ color: 'text.secondary', fontSize: 16 }} />
                          <Typography variant="caption">{formatPhone(supplier.phone)}</Typography>
                        </Box>
                      )}
                      {supplier.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <EmailIcon fontSize="small" sx={{ color: 'text.secondary', fontSize: 16 }} />
                          <Typography variant="caption">{supplier.email}</Typography>
                        </Box>
                      )}
                      {supplier.city && supplier.state && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocationIcon fontSize="small" sx={{ color: 'text.secondary', fontSize: 16 }} />
                          <Typography variant="caption">{supplier.city}/{supplier.state}</Typography>
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={supplier.is_active ? 'Ativo' : 'Inativo'}
                      size="small"
                      color={supplier.is_active ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">
                      {supplier.transaction_count || 0}
                    </Typography>
                    {supplier.total_amount > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {new Intl.NumberFormat('pt-BR', {
                          style: 'currency',
                          currency: 'BRL'
                        }).format(supplier.total_amount)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenDialog('edit', supplier)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(supplier)}
                        disabled={supplier.transaction_count > 0}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </TableContainer>

      {/* Dialog de Criar/Editar */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {dialogMode === 'create' ? 'Novo Fornecedor' : 'Editar Fornecedor'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              {/* Informações Básicas */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Informações Básicas
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Nome"
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  inputProps={{ maxLength: 200 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Razão Social"
                  value={formData.legal_name}
                  onChange={(e) => handleFormChange('legal_name', e.target.value)}
                  inputProps={{ maxLength: 200 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="CPF/CNPJ"
                  value={formatDocument(formData.document)}
                  onChange={(e) => handleDocumentChange(e.target.value)}
                  placeholder="Somente números"
                  helperText="Digite apenas os números. Sem CPF/CNPJ, não é possível cadastrar dois fornecedores com o mesmo nome."
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Categoria</InputLabel>
                  <Select
                    value={formData.category}
                    onChange={(e) => handleFormChange('category', e.target.value)}
                    label="Categoria"
                  >
                    <MenuItem value="">Selecione...</MenuItem>
                    {SUPPLIER_CATEGORIES.map(cat => (
                      <MenuItem key={cat} value={cat}>{CATEGORY_LABELS[cat]}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Contato */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 1 }}>
                  Contato
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="email"
                  label="Email"
                  value={formData.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Telefone"
                  value={formatPhone(formData.phone)}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  placeholder="(00) 00000-0000"
                  helperText="Digite apenas os números"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>

              {/* Endereço */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 1 }}>
                  Endereço
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Endereço"
                  value={formData.address}
                  onChange={(e) => handleFormChange('address', e.target.value)}
                  multiline
                  rows={2}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationIcon />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  label="Cidade"
                  value={formData.city}
                  onChange={(e) => handleFormChange('city', e.target.value)}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Estado</InputLabel>
                  <Select
                    value={formData.state}
                    onChange={(e) => handleFormChange('state', e.target.value)}
                    label="Estado"
                  >
                    <MenuItem value="">Selecione...</MenuItem>
                    {BRAZILIAN_STATES.map(state => (
                      <MenuItem key={state} value={state}>{state}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={4}>
                <TextField
                  fullWidth
                  label="CEP"
                  value={formData.zip_code}
                  onChange={(e) => handleZipCodeChange(e.target.value)}
                  placeholder="00000-000"
                  helperText="Somente números"
                />
              </Grid>

              {/* Dados Bancários */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 1 }}>
                  Dados Bancários
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Banco"
                  value={formData.bank_name}
                  onChange={(e) => handleFormChange('bank_name', e.target.value)}
                  inputProps={{ maxLength: 100 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BankIcon />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Conta Bancária"
                  value={formData.bank_account}
                  onChange={(e) => handleFormChange('bank_account', e.target.value)}
                  inputProps={{ maxLength: 50 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Chave PIX"
                  value={formData.pix_key}
                  onChange={(e) => handleFormChange('pix_key', e.target.value)}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>

              {/* Observações */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Observações"
                  value={formData.notes}
                  onChange={(e) => handleFormChange('notes', e.target.value)}
                  multiline
                  rows={3}
                />
              </Grid>

              {/* Status */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={(e) => handleFormChange('is_active', e.target.checked)}
                    />
                  }
                  label="Fornecedor Ativo"
                />
              </Grid>
            </Grid>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button variant="contained" onClick={() => runSubmit(handleSubmit)} disabled={submitting}>
            {dialogMode === 'create' ? 'Criar' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SuppliersTab;
